// src/components/popups/BoostPromotionDrawer.js
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './BoostPromotionDrawer.css';

import likeIcon from '../../assets/like-icon.png';
import commentIcon from '../../assets/comment-icon.png';
import tipIcon from '../../assets/tip-icon.png';
import viewIcon from '../../assets/viewcount-icon.png';
import shareIcon from '../../assets/share-icon.png';
import saveIcon from '../../assets/saved-icon.png';

const PREVIEW_ACTIONS = [
  { key: 'likes', label: 'Likes', icon: likeIcon, target: 1500 },
  { key: 'comments', label: 'Comments', icon: commentIcon, target: 2600 },
  { key: 'tips', label: 'Tips', icon: tipIcon, target: 20 },
  { key: 'views', label: 'Views', icon: viewIcon, target: 20000 },
  { key: 'share', label: 'Share', icon: shareIcon },
  { key: 'save', label: 'Save', icon: saveIcon },
];

function formatPreviewCount(value) {
  if (value < 1000) return String(value);

  return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
}

export default function BoostPromotionDrawer({
  open,
  onClose,
  onBoost,
}) {
  const [progress, setProgress] = useState(0);
  const panelRef = useRef(null);
  const laterButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Animate the demo counts once whenever the drawer opens.
  useEffect(() => {
    if (!open) return;

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    if (reducedMotion.matches) {
      setProgress(1);
      return;
    }

    setProgress(0);

    let frameId;
    let startedAt = null;

    const duration = 2200;

    const animate = (time) => {
      if (startedAt === null) startedAt = time;

      const elapsed = Math.min((time - startedAt) / duration, 1);

      // Ease out: quick growth that gently settles.
      setProgress(1 - Math.pow(1 - elapsed, 3));

      if (elapsed < 1) {
        frameId = window.requestAnimationFrame(animate);
      }
    };

    frameId = window.requestAnimationFrame(animate);

    return () => window.cancelAnimationFrame(frameId);
  }, [open]);

  // Keep keyboard focus inside the drawer.
  useEffect(() => {
    if (!open) return;

    const previousFocus = document.activeElement;
    laterButtonRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const buttons = panelRef.current?.querySelectorAll(
        'button:not([disabled])'
      );

      if (!buttons?.length) return;

      const first = buttons[0];
      const last = buttons[buttons.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Avoid moving focus away from a newly opened StakeDrawer.
      if (
        previousFocus instanceof HTMLElement &&
        previousFocus.isConnected &&
        (
          document.activeElement === document.body ||
          panelRef.current?.contains(document.activeElement)
        )
      ) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="bf-boost-promo-backdrop"
      onClick={() => onClose?.()}
    >
      <div
        ref={panelRef}
        className="bf-boost-promo-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bf-boost-promo-title"
        aria-describedby="bf-boost-promo-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bf-boost-promo-handle" aria-hidden="true" />

        <div className="bf-boost-promo-preview">
          <div className="bf-boost-promo-preview-label">
            A little extra visibility
          </div>

          <div
            className="bf-boost-promo-actions"
            aria-hidden="true"
          >
            {PREVIEW_ACTIONS.map((action) => (
              <div
                key={action.key}
                className={`bf-boost-promo-action bf-boost-promo-action--${action.key}`}
              >
                <img src={action.icon} alt="" />

                {typeof action.target === 'number' && (
                  <span>
                    {formatPreviewCount(
                      Math.round(action.target * progress)
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>

          <p className="bf-boost-promo-preview-note">
            Illustrative preview · Results vary
          </p>
        </div>

        <h2 id="bf-boost-promo-title">
          Help more Pioneers discover your post
        </h2>

        <p
          id="bf-boost-promo-description"
          className="bf-boost-promo-description"
        >
          Boost this post to give it extra visibility in the
          BarterFi feed. Choose how much Pi to stake and how
          long your boost runs.
        </p>

        <p className="bf-boost-promo-support">
          You’ll see the stake amount, duration and fee before
          confirming.
        </p>

        <div className="bf-boost-promo-buttons">
          <button
            type="button"
            className="bf-boost-promo-primary"
            onClick={() => onBoost?.()}
          >
            Boost now
          </button>

          <button
            ref={laterButtonRef}
            type="button"
            className="bf-boost-promo-secondary"
            onClick={() => onClose?.()}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
